import { useMemo, useRef, useState } from "react";
import { clsx } from "clsx";
import { IconPhoto, IconX, IconTrash, IconChevronDown, IconChevronUp, IconPlus } from "@tabler/icons-react";
import { Text } from "../../../../components/common/typography";
import { IconPill } from "../../../../components/common/icon-pill";
import { Select } from "../../../../components/forms/select";
import { RequiredMark } from "../../../../components/common/required-mark";
import { useProductConditions } from "../../hooks/useProductConditions";
import { showErrorToast } from "../../../../utils/toasts";

const ALLOWED_MIME = new Set(["image/jpeg", "image/png", "image/webp"]);

export interface VariantPricingTierEdit {
  id: number;
  minQty: string;
  price: string;
}

export interface VariantRowEdit {
  sku: string;
  stock: string;
  /** Product condition (e.g. New, Refurbished) — attribute-value ID as string. */
  conditionId?: string;
  /** Free-text variant model name. */
  model?: string;
  /** File queued for upload — actual GCS upload runs at submit time. */
  imageFile?: File;
  /** Local object URL for the thumbnail; revoked when cleared. */
  imagePreviewUrl?: string;
  /** Volume pricing tiers for this variant. */
  pricingTiers: VariantPricingTierEdit[];
}

export function VariantPricingRow({
  variantKey,
  variant,
  edit,
  onUpdate,
  onRemove,
  uploadProgress,
  readOnly = false,
  fieldErrors,
}: {
  variantKey?: string;
  variant: string;
  edit: VariantRowEdit;
  onUpdate: (patch: Partial<VariantRowEdit>) => void;
  onRemove: () => void;
  /** 0..100 while uploading at submit time, or undefined when idle. */
  uploadProgress?: number;
  readOnly?: boolean;
  fieldErrors?: Set<string>;
}) {
  const hasErr = (field: string) => variantKey != null && fieldErrors?.has(`variant:${variantKey}:${field}`) === true;
  const errBorder = "!border-accentError";
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploading = uploadProgress != null;
  const { data: conditions, isLoading: conditionsLoading } = useProductConditions();
  const conditionOptions = useMemo(
    () => (conditions ?? []).map((c) => ({ value: String(c.id), label: c.name })),
    [conditions],
  );
  const [tiersExpanded, setTiersExpanded] = useState(true);

  function handleImageChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (!ALLOWED_MIME.has(file.type)) {
      showErrorToast("Use JPEG, PNG, or WebP images.");
      return;
    }

    if (edit.imagePreviewUrl) URL.revokeObjectURL(edit.imagePreviewUrl);
    const previewUrl = URL.createObjectURL(file);
    onUpdate({ imageFile: file, imagePreviewUrl: previewUrl });
  }

  function clearImage() {
    if (edit.imagePreviewUrl) URL.revokeObjectURL(edit.imagePreviewUrl);
    onUpdate({ imageFile: undefined, imagePreviewUrl: undefined });
  }

  const tiers = edit.pricingTiers ?? [];

  function updateTier(tierId: number, patch: Partial<VariantPricingTierEdit>) {
    onUpdate({
      pricingTiers: tiers.map((t) => (t.id === tierId ? { ...t, ...patch } : t)),
    });
  }

  function removeTier(tierId: number) {
    onUpdate({ pricingTiers: tiers.filter((t) => t.id !== tierId) });
  }

  function addTier() {
    onUpdate({
      pricingTiers: [...tiers, { id: Date.now(), minQty: "", price: "" }],
    });
  }

  const tierSummary =
    tiers.length === 0
      ? "No tiers"
      : tiers.length === 1
        ? `1 tier`
        : `${tiers.length} tiers`;

  return (
    <div className="flex flex-col border-t border-borderDefault">
      <div className="grid grid-cols-[100px_1fr_1fr_1fr_1fr_36px] items-center gap-3 px-6 py-4">
        <div>
          {edit.imagePreviewUrl ? (
            <div className="relative size-12 overflow-hidden rounded-xl border border-borderDefault">
              <img src={edit.imagePreviewUrl} alt={variant} className="size-full object-cover" />
              {uploading && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-textDark/60 text-bgWhite">
                  <Text variant="bodyXsBold" tone="inherit">{uploadProgress}%</Text>
                </div>
              )}
              {!uploading && !readOnly && (
                <button
                  onClick={clearImage}
                  className="absolute right-0.5 top-0.5 flex size-4 items-center justify-center rounded-full bg-bgWhite shadow"
                  aria-label="Remove image"
                >
                  <IconX size={8} className="text-textDark" />
                </button>
              )}
            </div>
          ) : readOnly ? (
            <div className="flex size-12 items-center justify-center rounded-xl border border-borderDefault bg-bgLight">
              <IconPhoto size={16} className="text-textMuted" />
            </div>
          ) : (
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="flex items-center gap-1.5 rounded-xl border border-borderDefault bg-bgWhite px-3 py-2 transition-colors hover:bg-bgLight disabled:opacity-50"
            >
              <IconPhoto size={14} className="text-textBody" />
              <Text variant="bodyXsBold" tone="body">Add</Text>
            </button>
          )}
          {!readOnly && (
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={handleImageChange}
            />
          )}
        </div>
        <div className="flex flex-col gap-1">
          <Text variant="bodyXs" tone="muted">Variant</Text>
          <Text variant="bodyBold" tone="dark">{variant}</Text>
        </div>
        <div className="flex flex-col gap-1">
          <Text variant="bodyXs" tone="muted">SKU</Text>
          <div className={clsx("flex h-10 items-center rounded-2xl border px-3 transition-colors focus-within:border-textBody", hasErr("sku") ? errBorder : "border-borderDefault")}>
            <input
              value={edit.sku}
              onChange={(e) => onUpdate({ sku: e.target.value })}
              placeholder="e.g. SKU-001"
              disabled={readOnly}
              className="w-full border-0 bg-transparent text-bodySm text-textDark outline-none placeholder:text-textBody disabled:cursor-not-allowed"
            />
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <Text variant="bodyXs" tone="muted">Stock</Text>
          <div className={clsx("flex h-10 items-center rounded-2xl border px-3 transition-colors focus-within:border-textBody", hasErr("stock") ? errBorder : "border-borderDefault")}>
            <input
              value={edit.stock}
              onChange={(e) => onUpdate({ stock: e.target.value })}
              placeholder="0"
              disabled={readOnly}
              className="w-full border-0 bg-transparent text-bodySm text-textDark outline-none placeholder:text-textBody disabled:cursor-not-allowed"
            />
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <Text variant="bodyXs" tone="muted">Condition</Text>
          <Select
            name={`condition-${variant}`}
            value={edit.conditionId ?? ""}
            onChange={(e) => onUpdate({ conditionId: e.target.value })}
            options={conditionOptions}
            placeholder={conditionsLoading ? "…" : "Condition"}
            disabled={readOnly || conditionsLoading}
            density="compact"
            error={hasErr("conditionId") ? " " : undefined}
          />
        </div>
        {!readOnly ? (
          <IconPill
            as="button"
            size={24}
            tone="ghost"
            aria-label="Remove variant"
            onClick={onRemove}
            className="!bg-transparent !text-textDark transition-all hover:[&>svg]:stroke-[2.5]"
          >
            <IconTrash size={16} />
          </IconPill>
        ) : <div />}
      </div>

      {/* Pricing tiers */}
      <div className="border-t border-borderLight px-6 py-3">
        <button
          type="button"
          onClick={() => setTiersExpanded((prev) => !prev)}
          className="flex items-center gap-1.5"
        >
          <Text variant="bodySmBold" tone="body">Pricing tiers</Text>
          <Text variant="bodyXs" tone="muted">({tierSummary})</Text>
          {tiersExpanded ? (
            <IconChevronUp size={14} className="text-textBody" />
          ) : (
            <IconChevronDown size={14} className="text-textBody" />
          )}
        </button>

        {tiersExpanded && (
          <div className="mt-3 flex flex-col gap-2">
            {tiers.length > 0 && (
              <div className="grid grid-cols-[1fr_1fr_36px] gap-2">
                <Text variant="bodyXsBold" tone="muted">
                  Min qty
                  {!readOnly && <RequiredMark />}
                </Text>
                <Text variant="bodyXsBold" tone="muted">
                  Price (QAR)
                  {!readOnly && <RequiredMark />}
                </Text>
                <div />
              </div>
            )}
            {tiers.map((tier) => (
              <div key={tier.id} className="grid grid-cols-[1fr_1fr_36px] items-center gap-2">
                <div className={clsx("rounded-xl border px-2.5 py-1.5 transition-colors focus-within:border-textBody", hasErr(`tier:${tier.id}:minQty`) ? errBorder : "border-borderDefault")}>
                  <input
                    value={tier.minQty}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === "" || /^\d+$/.test(v)) updateTier(tier.id, { minQty: v });
                    }}
                    placeholder="e.g. 1"
                    inputMode="numeric"
                    disabled={readOnly}
                    className="w-full border-0 bg-transparent text-bodySm text-textDark outline-none placeholder:text-textBody disabled:cursor-not-allowed"
                  />
                </div>
                <div className={clsx("rounded-xl border px-2.5 py-1.5 transition-colors focus-within:border-textBody", hasErr(`tier:${tier.id}:price`) ? errBorder : "border-borderDefault")}>
                  <input
                    value={tier.price}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === "" || /^\d*\.?\d*$/.test(v)) updateTier(tier.id, { price: v });
                    }}
                    placeholder="0.00"
                    inputMode="decimal"
                    disabled={readOnly}
                    className="w-full border-0 bg-transparent text-bodySm text-textDark outline-none placeholder:text-textBody disabled:cursor-not-allowed"
                  />
                </div>
                {!readOnly && tiers.length > 1 && (
                  <IconPill
                    as="button"
                    size={24}
                    tone="ghost"
                    aria-label="Remove tier"
                    onClick={() => removeTier(tier.id)}
                    className="!bg-transparent !text-textMuted hover:!text-textDark"
                  >
                    <IconX size={14} />
                  </IconPill>
                )}
              </div>
            ))}
            {!readOnly && (
              <button
                type="button"
                onClick={addTier}
                className="mt-1 flex items-center gap-1 self-start"
              >
                <IconPlus size={12} className="text-textBody" />
                <Text variant="bodySmBold" tone="body">Add tier</Text>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
